
import { useState, useEffect } from 'react';
import { emailService } from '@/lib/email/emailService';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';

export const useResetPassword = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(60);

  // Handle countdown timer for rate limiting
  useEffect(() => {
    let timer: number | null = null;
    
    if (rateLimited && cooldownTime > 0) {
      timer = window.setInterval(() => {
        setCooldownTime(prev => Math.max(0, prev - 1));
      }, 1000);
    }
    
    // If cooldown expires, reset rate limiting
    if (cooldownTime === 0 && rateLimited) {
      setRateLimited(false);
      setCooldownTime(60);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [rateLimited, cooldownTime]);

  // Handle URL parameters to pre-populate email
  useEffect(() => {
    // Get email from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
    
    // Custom event to set the email from other components
    const handleSetEmail = (event: CustomEvent) => {
      if (event.detail) {
        setEmail(event.detail);
      }
    };

    // Listen for the custom event
    window.addEventListener('set-reset-email' as any, handleSetEmail as EventListener);
    
    return () => {
      window.removeEventListener('set-reset-email' as any, handleSetEmail as EventListener);
    };
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Email validation
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Check if rate limited
    if (rateLimited) {
      setError(`Too many requests. Please wait ${cooldownTime} seconds before trying again.`);
      return;
    }
    
    setLoading(true);
    
    try {
      logger.info({
        message: "Password reset requested",
        context: { email }
      });
      
      // Use Supabase's direct reset password functionality
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (resetError) {
        // Check if this is a rate limit issue
        if (resetError.message?.toLowerCase().includes('rate limit') || 
            resetError.status === 429 ||
            resetError.message?.includes('exceeded')) {
          setRateLimited(true);
          setCooldownTime(60);
          throw new Error("For security reasons, we limit how often password reset emails can be sent. Please wait 1 minute before trying again.");
        }
        
        throw resetError;
      }
      
      setResetSent(true);
      toast({
        title: "Reset link sent",
        description: "Check your email for a password reset link. If you don't see it, check your spam folder.",
      });
      
      logger.info({
        message: "Password reset email sent successfully via Supabase",
        context: { email }
      });
    } catch (error: any) {
      logger.error({
        message: "Failed to send password reset email",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { email }
      });
      
      // Check if this might be a rate limit issue that wasn't caught earlier
      if (error.message?.toLowerCase().includes('rate limit') || 
          error.message?.includes('429') ||
          error.message?.includes('exceeded')) {
        setRateLimited(true);
        setCooldownTime(60);
        setError("For security reasons, we limit how often password reset emails can be sent. Please wait 1 minute before trying again.");
      } else {
        setError(error.message || 'Failed to send password reset email. Please try again later.');
      }
      
      toast({
        title: "Error",
        description: error.message || 'Failed to send password reset email',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    loading,
    error,
    resetSent,
    rateLimited,
    cooldownTime,
    handleResetPassword
  };
};
